import { useState } from "react";
import { DCUIContent, DCUIContentStart, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { DCUIThemingStartPageValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { DCUIThemingPreviewContainer } from "../../../../common/dcui-theming-preview-container/dcui-theming-preview-container";
import { DCUIThemingStartPageContentSection } from "./dcui-theming-start-page-content-section/dcui-theming-start-page-content-section";
import { DCUIThemingStartPageButtonsSection } from "./dcui-theming-start-page-buttons-section/dcui-theming-start-page-buttons-section";
import { DCUIThemingStartPageCaptureStepsSection } from "./dcui-theming-start-page-capture-steps-section/dcui-theming-start-page-capture-steps-section";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingStartPageTabContainerProps = {
    isDisabled: boolean;
    validationErrors: DCUIThemingStartPageValidationErrors;
    dcuiContent: DCUIContent;
    dcuiTheme: DCUITheme;
    inheritedDCUIContent: DCUIContent;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    pageLevel: PageLevelTypes;
    documentCaptureSteps: Array<DCUIThemingStep>;
    previewEntityType: PreviewEntityTypes;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingStartPageTabContainer = (props: DCUIThemingStartPageTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        dcuiContent,
        dcuiTheme,
        inheritedDCUIContent,
        uploadFileToS3,
        pageLevel,
        documentCaptureSteps,
        previewEntityType,
        updateDCUIContentPropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateDCUIContentStartPageFieldValue = (
        key: keyof DCUIContentStart,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            start: {
                ...dcuiContent.start,
                [key]: value,
            },
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.content.title} showSeparator={true}>
                            <DCUIThemingStartPageContentSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                dcuiContent={dcuiContent}
                                inheritedDCUIContent={inheritedDCUIContent}
                                pageLevel={pageLevel}
                                validationErrors={validationErrors}
                                updateDCUIContentStartPageFieldValue={updateDCUIContentStartPageFieldValue}
                            />
                        </ThemingFormSection>
                        {documentCaptureSteps.length > 0 && (
                            <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.title} showSeparator={true}>
                                <DCUIThemingStartPageCaptureStepsSection
                                    dcuiContent={dcuiContent}
                                    inheritedDCUIContent={inheritedDCUIContent}
                                    uploadFileToS3={uploadFileToS3}
                                    pageLevel={pageLevel}
                                    isDisabled={isDisabled}
                                    labelWidth={labelWidth}
                                    layout={layout}
                                    validationErrors={validationErrors}
                                    documentCaptureSteps={documentCaptureSteps}
                                    updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                                />
                            </ThemingFormSection>
                        )}
                        <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.buttons.title} showSeparator={false}>
                            <DCUIThemingStartPageButtonsSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                dcuiContent={dcuiContent}
                                inheritedDCUIContent={inheritedDCUIContent}
                                pageLevel={pageLevel}
                                validationErrors={validationErrors}
                                updateDCUIContentStartPageFieldValue={updateDCUIContentStartPageFieldValue}
                            />
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    <DCUIThemingPreviewContainer
                        currentTab={DCUIThemingTabTypes.START_PAGE}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={previewEntityType}
                    />
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};